import type { CSSProperties } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type {
  CellKeyDownEvent,
  FullWidthCellKeyDownEvent,
  GetContextMenuItemsParams,
  GridApi,
  GridReadyEvent,
  ProcessCellForExportParams,
} from 'ag-grid-community'
import {
  CellStyleModule,
  ClientSideRowModelModule,
  ColumnApiModule,
  ColumnAutoSizeModule,
  GridStateModule,
  PaginationModule,
  RowApiModule,
  ScrollApiModule,
  TooltipModule,
} from 'ag-grid-community'
import {
  CellSelectionModule,
  ClipboardModule,
  ColumnMenuModule,
  ContextMenuModule,
  CsvExportModule,
  ServerSideRowModelModule,
} from 'ag-grid-enterprise'
import { AgGridReact } from 'ag-grid-react'
import { useIntl } from 'react-intl'

import {
  useAgGridMemory,
  type IgnorableProperties,
} from '@b-stock/fe-core/ag-grid'
import type { ManifestTableRow } from '@b-stock/fe-core/bstock-next/components'
import type { OrchestratedListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import { ddLogger } from '@b-stock/fe-core/bstock-react'

import { SpinnerOverlay } from '@/components/ag-grid/SpinnerOverlay'
import {
  registerAgGridModules,
  useAgGridLicenseReady,
} from '@/hooks/ag-grid/agGridSetup'
import { useColumnPinning } from '@/hooks/ag-grid/useColumnPinning'

import { useManifestTableTracking } from '../../hooks/useManifestTableTracking'
import { autoSizeWidthOverrides } from './columns/columnSpecs'
import {
  defaultColDef,
  getPinnedFieldPath,
  useColumnDefs,
} from './columns/useColumnDefs'
import type { ActiveSort } from './FooterRowCount'
import { getVisibleRowRange } from './getVisibleRowRange'
import { useManifestContext } from './ManifestContext'
import NoResultsTableBody from './NoResultsTableBody'
import type { useManifestTracking } from './tracking/useManifestTracking'
import type { HonestGridApi } from './types'
import { GridContainer, gridTheme, SCROLLBAR_WIDTH } from './ui'
import { useManifestPreviewRows } from './useManifestPreviewRows'

registerAgGridModules([
  CellSelectionModule,
  CellStyleModule,
  ClientSideRowModelModule,
  ClipboardModule,
  ColumnApiModule,
  ColumnAutoSizeModule,
  ColumnMenuModule,
  ContextMenuModule,
  CsvExportModule,
  GridStateModule,
  PaginationModule,
  RowApiModule,
  ScrollApiModule,
  ServerSideRowModelModule,
  TooltipModule,
])

const MEMORY_VERSION = 'v2'

const PROPERTIES_TO_IGNORE: IgnorableProperties[] = [
  'filter',
  'pagination',
  'sort',
]

/**
 * Return the set of colIds that have persisted widths for the given key.
 * Mirrors useAgGridMemory's internal key format and version check.
 */
function getPersistedColumnWidths(tableKey: string): Set<string> {
  try {
    const raw = localStorage.getItem(`${tableKey}--table-state`)
    if (!raw) return new Set()
    const parsed = JSON.parse(raw) as {
      version?: string
      state?: { columnSizing?: { columnSizingModel?: { colId: string }[] } }
    } | null
    if (parsed?.version !== MEMORY_VERSION) return new Set()
    const model = parsed.state?.columnSizing?.columnSizingModel
    return new Set(model?.map((c) => c.colId))
  } catch {
    return new Set()
  }
}

function getSelectedColumnIds(api: GridApi): string[] {
  // Set deduplicates columns that appear in multiple overlapping ranges
  return [
    ...new Set(
      api.getCellRanges()?.flatMap((r) => r.columns.map((c) => c.getColId())) ??
        []
    ),
  ]
}

type ManifestGridVariant = 'inline' | 'fullscreen'

type ManifestGridProps = {
  agGridTrackingProps: ReturnType<typeof useManifestTracking>['agGridProps']
  // Passed down rather than read via useFlag() here — the caller freezes this
  // at mount (see FullManifestPanel's isInfiniteScrollEnabledRef) so it can't
  // drift out of sync with the frozen `limit`/datasource that also depends on
  // it; reading it live here again would let domLayout/pagination react to a
  // mid-session LaunchDarkly update while cacheBlockSize/the datasource stay
  // on the old value.
  isInfiniteScrollEnabled: boolean
  isLoggedIn: boolean
  listing: OrchestratedListingDetails
  setActiveSort: (sort: ActiveSort | null) => void
  setPage: (page: number) => void
  stateLoadTrigger?: number
  variant?: ManifestGridVariant
}

export const ManifestGrid = ({
  agGridTrackingProps,
  isInfiniteScrollEnabled,
  isLoggedIn,
  listing,
  setActiveSort,
  setPage,
  stateLoadTrigger,
  variant = 'inline',
}: ManifestGridProps) => {
  const isAgGridReady = useAgGridLicenseReady()
  const { formatMessage } = useIntl()
  const { lot } = listing
  const {
    datasource,
    gridApi,
    isLoading,
    limit,
    setColumnDefs,
    setFilters,
    setGridApi,
    setSearch,
    setVisibleRange,
    total,
  } = useManifestContext()
  const isFullscreen = variant === 'fullscreen'
  // 'normal' gives a fixed-height scroll viewport; autoHeight has none (the grid
  // grows to fit its rows). reportVisibleRowRange keys off this directly so the
  // prop and the row-range strategy can't drift apart. Inline adopts the same
  // bounded/scrollable layout as fullscreen once infinite scroll is enabled
  // (see ENABLE_HP_MANIFEST_INFINITE_SCROLL) — otherwise it stays autoHeight,
  // bounded only by its 12-row page. Logged-out guests always keep the
  // content-sized autoHeight preview (a handful of static rows under
  // FadeOutMask), regardless of the flag.
  const isInlineInfiniteScroll =
    !isFullscreen && isInfiniteScrollEnabled && isLoggedIn
  const domLayout =
    isFullscreen || isInlineInfiniteScroll ? 'normal' : 'autoHeight'

  const pinnedColId = getPinnedFieldPath(lot)
  const columnPinningProps = useColumnPinning({
    colId: pinnedColId,
    gridApi,
    pinDirection: 'left',
  })
  const columnDefs = useColumnDefs({
    currency: listing.currency,
    isLoggedIn,
    isMobileAtMount: columnPinningProps.isMobileAtMountRef.current,
    lot,
  })

  // columnDefs must be referentially stable for the lifetime of the grid —
  // AG Grid resets its internal column state whenever it sees a new columnDefs
  // reference, which wipes the user's persisted widths/order/visibility.
  const [initialColumnDefs] = useState(columnDefs)
  useEffect(() => {
    setColumnDefs(initialColumnDefs)
  }, [initialColumnDefs, setColumnDefs])
  useEffect(() => {
    if (columnDefs !== initialColumnDefs) {
      ddLogger.error(
        'ManifestGrid: columnDefs changed after mount; persisted column state will be invalidated.'
      )
    }
  }, [columnDefs, initialColumnDefs])

  const {
    onManifestCellCopied,
    onManifestReset,
    onManifestSearchClear,
    onSortingChanged,
  } = useManifestTableTracking(listing)

  const memoryKey = `BStock::ManifestTable::${listing.storefrontId}`
  const agGridMemoryProps = useAgGridMemory(memoryKey, {
    propertiesToIgnore: PROPERTIES_TO_IGNORE,
    version: MEMORY_VERSION,
  })

  const { onGridReady: restoreColumnMemory } = agGridMemoryProps
  const { onGridReadyAfterMemory } = columnPinningProps

  const restorePersistedColumnState = useCallback(
    (event: GridReadyEvent) => {
      restoreColumnMemory(event)
      // Defends against a hide:true persisted for pinnedColId — ag-Grid's
      // column-state restore (api.setState) bypasses lockVisible, so a bad
      // localStorage entry could otherwise re-hide this required column with
      // no in-app recovery path (ColumnSelect's checkbox stays disabled).
      event.api.setColumnsVisible([pinnedColId], true)
      onGridReadyAfterMemory(event)
    },
    [onGridReadyAfterMemory, pinnedColId, restoreColumnMemory]
  )

  const gridReadyEventRef = useRef<GridReadyEvent | null>(null)
  const lastStateLoadTriggerRef = useRef(stateLoadTrigger)
  // ag-Grid's server-side row model can emit sortChanged more than once for a
  // single user sort action (once on column-state change, again once the
  // server-side store purges and refetches) — this guards manifest_sort
  // against re-firing for the same colId/direction pair.
  const lastFiredSortKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (stateLoadTrigger === lastStateLoadTriggerRef.current) return
    lastStateLoadTriggerRef.current = stateLoadTrigger
    const event = gridReadyEventRef.current
    if (event) restorePersistedColumnState(event)
  }, [restorePersistedColumnState, stateLoadTrigger])

  const previewRows = useManifestPreviewRows(listing)

  const reportVisibleRowRange = (api: HonestGridApi) => {
    const range = getVisibleRowRange(api, { domLayout, limit })
    if (range) setVisibleRange(range)
  }

  const noRowsOverlayComponent = useMemo(
    () => () => (
      <NoResultsTableBody
        resetFilters={() => {
          setFilters({})
          setSearch('')
          onManifestReset()
          onManifestSearchClear()
        }}
      />
    ),
    [onManifestReset, onManifestSearchClear, setFilters, setSearch]
  )

  const formatCellForExport = useCallback(
    (params: ProcessCellForExportParams<ManifestTableRow>): string =>
      params.formatValue(params.value),
    []
  )

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams<ManifestTableRow>) => {
      return [
        {
          name: formatMessage({
            id: 'Listing.manifestAgGrid.contextMenu.copy',
          }),
          action: () => {
            params.api.copySelectedRangeToClipboard()
            onManifestCellCopied(
              getSelectedColumnIds(params.api),
              variant,
              'context_menu'
            )
          },
        },
      ]
    },
    [formatMessage, onManifestCellCopied, variant]
  )

  const handleCellKeyDown = useCallback(
    (e: CellKeyDownEvent<ManifestTableRow> | FullWidthCellKeyDownEvent) => {
      const ke = e.event
      if (
        ke instanceof KeyboardEvent &&
        ke.key === 'c' &&
        (ke.ctrlKey || ke.metaKey)
      ) {
        onManifestCellCopied(getSelectedColumnIds(e.api), variant, 'keyboard')
      }
    },
    [onManifestCellCopied, variant]
  )

  const boundedScrollStyle:
    | (CSSProperties & { '--manifest-row-count'?: number })
    | undefined = isInlineInfiniteScroll
    ? { '--manifest-row-count': total }
    : undefined

  // Wait for the AG Grid Enterprise license to be registered before mounting
  // the grid — mounting earlier races LicenseManager.setLicenseKey and can
  // log a spurious "no license" console error.
  if (!isAgGridReady) {
    return (
      <GridContainer
        $boundedInlineScroll={isInlineInfiniteScroll}
        $fillHeight={isFullscreen}
        style={boundedScrollStyle}
      />
    )
  }

  return (
    <GridContainer
      $boundedInlineScroll={isInlineInfiniteScroll}
      $fillHeight={isFullscreen}
      style={boundedScrollStyle}
    >
      <AgGridReact
        {...agGridTrackingProps}
        onBodyScroll={(e) => {
          agGridTrackingProps.onBodyScroll(e)
          reportVisibleRowRange(e.api)
        }}
        columnDefs={initialColumnDefs}
        defaultColDef={defaultColDef}
        domLayout={domLayout}
        loadingOverlayComponent={SpinnerOverlay}
        maintainColumnOrder={agGridMemoryProps.maintainColumnOrder}
        noRowsOverlayComponent={noRowsOverlayComponent}
        onFirstDataRendered={(e) => {
          const persisted = getPersistedColumnWidths(memoryKey)
          e.api.autoSizeColumns(
            e.api
              .getColumns()
              ?.filter((col) => !persisted.has(col.getColId())) ?? []
          )
          e.api.setColumnWidths(
            autoSizeWidthOverrides.filter((o) => !persisted.has(o.key))
          )
          reportVisibleRowRange(e.api)
        }}
        onGridReady={(e) => {
          gridReadyEventRef.current = e
          restorePersistedColumnState(e)
          setGridApi(e.api)
        }}
        onPaginationChanged={(e) => {
          setPage(e.api.paginationGetCurrentPage() + 1)
        }}
        onSortChanged={(e) => {
          const sortState = e.api.getColumnState().find((s) => s.sort != null)
          setActiveSort(
            sortState
              ? { colId: sortState.colId, direction: sortState.sort ?? 'asc' }
              : null
          )

          const sortKey = sortState
            ? `${sortState.colId}:${sortState.sort}`
            : null
          if (sortKey === lastFiredSortKeyRef.current) return
          lastFiredSortKeyRef.current = sortKey

          onSortingChanged(
            sortState
              ? [{ id: sortState.colId, desc: sortState.sort === 'desc' }]
              : []
          )
        }}
        onGridSizeChanged={(e) => reportVisibleRowRange(e.api)}
        onModelUpdated={(e) => reportVisibleRowRange(e.api)}
        onStateUpdated={
          isLoggedIn ? (e) => agGridMemoryProps.onStateUpdated?.(e) : undefined
        }
        scrollbarWidth={SCROLLBAR_WIDTH}
        // suppressColumnVirtualisation needed to auto-size offscreen columns
        suppressColumnVirtualisation
        theme={gridTheme}
        tooltipHideDelay={1_000_000}
        tooltipMouseTrack
        tooltipShowDelay={300}
        processCellForClipboard={formatCellForExport}
        cellSelection
        getContextMenuItems={getContextMenuItems}
        onCellKeyDown={handleCellKeyDown}
        {...(isLoggedIn
          ? {
              cacheBlockSize: limit,
              loading: isLoading,
              rowModelType: 'serverSide',
              serverSideDatasource: datasource,
              ...(isFullscreen || isInlineInfiniteScroll
                ? {}
                : {
                    pagination: true,
                    paginationPageSize: limit,
                    suppressPaginationPanel: true,
                  }),
            }
          : {
              rowModelType: 'clientSide',
              rowData: previewRows,
              suppressMovableColumns: true,
            })}
      />
    </GridContainer>
  )
}
